import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import QuizIcon from "@mui/icons-material/Quiz";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { NavLink } from "react-router-dom";
import axios from "axios";
import liff from "@line/liff";
import Header from "../components/common/Header";
import Footer from "../components/common/Footer";
import { ensureLiffInit } from "../liffClient";

type Question = {
  image_url: string;
  choices: string[];
  answer: string;
  meaning: string;
  choice_meanings: Record<string, string>;
};

const LANG_LABELS: Record<string, string> = {
  english: "英語",
  french: "フランス語",
  japanese: "日本語",
};

const LEVEL_ORDER = ["beginner", "intermediate", "advanced"] as const;

const LEVEL_LABELS: Record<string, string> = {
  beginner: "初級",
  intermediate: "中級",
  advanced: "上級",
};

// 各言語で 初級/中級/上級 がどの試験の級に相当するかの目安
const LEVEL_HINT: Record<string, string> = {
  english: "初級=英検4〜5級 / 中級=英検準2〜2級 / 上級=英検準1級〜",
  french: "初級=仏検4〜5級 / 中級=仏検準2〜2級 / 上級=仏検準1級〜",
  japanese: "初級=JLPT N4〜N5 / 中級=JLPT N2〜N3 / 上級=JLPT N1〜",
};

// SpeechSynthesis に渡す BCP-47 の言語コード
const SPEECH_LANG: Record<string, string> = {
  english: "en-US",
  french: "fr-FR",
  japanese: "ja-JP",
};

const speak = (word: string, lang: string) => {
  const instance = new SpeechSynthesisUtterance(word);
  instance.lang = SPEECH_LANG[lang] ?? "en-US";
  window.speechSynthesis.speak(instance);
};

type Phase = "setup" | "playing" | "finished";

export default function Quiz() {
  const [languages, setLanguages] = useState<string[]>([]);
  const [lang, setLang] = useState("");
  const [availableLevels, setAvailableLevels] = useState<string[]>([...LEVEL_ORDER]);
  const [level, setLevel] = useState<string>("beginner");
  const [phase, setPhase] = useState<Phase>("setup");
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState<Question | null>(null);
  const [answered, setAnswered] = useState(0);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);

  useEffect(() => {
    axios
      .post("/v1/api/quiz/languages", {})
      .then((response) => {
        const fetched: string[] = response.data.languages ?? [];
        setLanguages(fetched);
        if (fetched.length > 0) setLang(fetched[0]);
      })
      .catch((error) => console.error("Error fetching quiz languages:", error));

    // ログイン状態の確認はあくまで背景で試みるだけで、失敗してもクイズ自体は続行できる
    ensureLiffInit().then((ok) => setLoggedIn(ok && liff.isLoggedIn()));
  }, []);

  // 言語が変わったら、その言語で出題可能なレベルを取得する
  useEffect(() => {
    if (!lang) return;
    axios
      .post("/v1/api/quiz/levels", { lang })
      .then((response) => {
        const levels: string[] = response.data.levels ?? [...LEVEL_ORDER];
        setAvailableLevels(levels);
        setLevel((prev) => (levels.includes(prev) ? prev : levels[0] ?? "beginner"));
      })
      .catch((error) => console.error("Error fetching quiz levels:", error));
  }, [lang]);

  const handleLogin = async () => {
    const ok = await ensureLiffInit();
    if (!ok) {
      setLoginFailed(true);
      return;
    }
    liff.login();
  };

  const fetchQuestion = async () => {
    setLoading(true);
    setSelected(null);
    try {
      const response = await axios.post("/v1/api/quiz/question", { lang, level });
      setCurrent(response.data);
    } catch (error) {
      console.error("Error fetching quiz question:", error);
    } finally {
      setLoading(false);
    }
  };

  const startQuiz = async () => {
    if (!lang) return;
    setScore(0);
    setAnswered(0);
    setPhase("playing");
    await fetchQuestion();
  };

  const handleChoice = async (choice: string) => {
    if (selected || !current) return;
    setSelected(choice);
    setAnswered((n) => n + 1);

    const correct = choice === current.answer;
    if (correct) {
      setScore((s) => s + 1);
    } else if (loggedIn) {
      try {
        const profile = await liff.getProfile();
        await axios.post("/v1/api/quiz/mistake/record", {
          userId: profile.userId,
          word: current.answer,
          imageUrl: current.image_url,
          meaning: current.meaning,
          lang,
        });
      } catch (error) {
        console.error("Error recording quiz mistake:", error);
      }
    }
  };

  // 回答後は自動で進めず、ユーザーが「次へ」を押したタイミングで次の問題へ進む。
  // 問題数の上限はなく、「終了する」を押すまで無限に出題する
  const goNext = () => {
    fetchQuestion();
  };

  const finishQuiz = () => {
    setPhase(answered === 0 ? "setup" : "finished");
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f1f5f9", display: "flex", flexDirection: "column" }}>
      <Box sx={{ width: "100%", position: "sticky", top: "0", zIndex: 100 }}>
        <Header />
      </Box>

      <Box sx={{ maxWidth: 560, mx: "auto", px: 2, pt: 4, pb: 2, flex: 1, width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #4338ca, #6366f1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(99,102,241,0.4)",
            }}
          >
            <QuizIcon sx={{ color: "white", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.4rem", color: "#1e293b", lineHeight: 1.2 }}>
              画像クイズ
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>
              画像を見て単語を4択で当てよう
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            p: 2.5,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
            border: "1px solid rgba(226,232,240,0.8)",
          }}
        >
          {phase === "setup" && (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#475569", mb: 1.5 }}>
                言語を選んでください
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={lang}
                onChange={(_e, value) => value && setLang(value)}
                sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}
              >
                {languages.map((l) => (
                  <ToggleButton
                    key={l}
                    value={l}
                    sx={{
                      textTransform: "none",
                      borderRadius: "10px !important",
                      fontWeight: 600,
                      "&.Mui-selected": {
                        background: "linear-gradient(135deg, #4338ca, #6366f1)",
                        color: "white",
                        "&:hover": { background: "linear-gradient(135deg, #3730a3, #4f46e5)" },
                      },
                    }}
                  >
                    {LANG_LABELS[l] ?? l}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#475569", mb: 1.5 }}>
                レベルを選んでください
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={level}
                onChange={(_e, value) => value && setLevel(value)}
                sx={{ flexWrap: "wrap", gap: 1 }}
              >
                {LEVEL_ORDER.filter((l) => availableLevels.includes(l)).map((l) => (
                  <ToggleButton
                    key={l}
                    value={l}
                    sx={{
                      textTransform: "none",
                      borderRadius: "10px !important",
                      fontWeight: 600,
                      "&.Mui-selected": {
                        background: "linear-gradient(135deg, #4338ca, #6366f1)",
                        color: "white",
                        "&:hover": { background: "linear-gradient(135deg, #3730a3, #4f46e5)" },
                      },
                    }}
                  >
                    {LEVEL_LABELS[l] ?? l}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography sx={{ color: "#94a3b8", fontSize: "0.72rem", mt: 1, mb: 3 }}>
                {LEVEL_HINT[lang] ?? "難易度の目安です"}
              </Typography>

              {!loggedIn && (
                <Typography sx={{ color: "#94a3b8", fontSize: "0.78rem", mb: 2 }}>
                  ログインなしで参加できます。ログインすると間違えた問題を後から見返せます。
                </Typography>
              )}

              <Button
                fullWidth
                disabled={loading || !lang}
                onClick={startQuiz}
                variant="contained"
                sx={{
                  background: "linear-gradient(135deg, #4338ca, #6366f1)",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.5,
                  boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
                  "&:hover": { background: "linear-gradient(135deg, #3730a3, #4f46e5)" },
                }}
              >
                {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : "クイズを始める"}
              </Button>
            </Box>
          )}

          {phase === "playing" && loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: "#6366f1" }} />
            </Box>
          )}

          {phase === "playing" && !loading && current && (
            <Box>
              <Typography sx={{ color: "#94a3b8", fontSize: "0.8rem", fontWeight: 600, mb: 2 }}>
                正解 {score} / 回答 {answered}
              </Typography>

              <Box
                component="img"
                src={current.image_url}
                alt="quiz"
                sx={{
                  width: "100%",
                  height: 220,
                  objectFit: "cover",
                  borderRadius: "14px",
                  mb: current.meaning ? 1.5 : 3,
                  border: "2px solid #f1f5f9",
                }}
              />

              {current.meaning && (
                <Typography
                  sx={{
                    textAlign: "center",
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    color: "#4338ca",
                    mb: 3,
                  }}
                >
                  {current.meaning}
                </Typography>
              )}

              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                {current.choices.map((choice) => {
                  const isSelected = selected === choice;
                  const isAnswer = choice === current.answer;
                  const showResult = Boolean(selected);
                  let background = "#f8fafc";
                  let color = "#1e293b";
                  let border = "1px solid #e2e8f0";
                  if (showResult && isAnswer) {
                    background = "#dcfce7";
                    color = "#166534";
                    border = "1px solid #86efac";
                  } else if (showResult && isSelected && !isAnswer) {
                    background = "#fee2e2";
                    color = "#991b1b";
                    border = "1px solid #fca5a5";
                  }
                  const choiceMeaning = current.choice_meanings?.[choice];
                  return (
                    <Box key={choice} sx={{ position: "relative", display: "flex" }}>
                      <Button
                        onClick={() => handleChoice(choice)}
                        disabled={showResult}
                        sx={{
                          flex: 1,
                          backgroundColor: background,
                          color,
                          border,
                          borderRadius: "12px",
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "1rem",
                          py: 1.75,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.25,
                          "&:hover": { backgroundColor: showResult ? background : "#eef2ff" },
                          "&.Mui-disabled": { color, backgroundColor: background, borderColor: border },
                        }}
                      >
                        {choice}
                        {showResult && choiceMeaning && (
                          <Typography
                            component="span"
                            sx={{ fontSize: "0.75rem", fontWeight: 500, color: "inherit", opacity: 0.75 }}
                          >
                            {choiceMeaning}
                          </Typography>
                        )}
                      </Button>
                      {showResult && (
                        <IconButton
                          onClick={() => speak(choice, lang)}
                          sx={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            width: 28,
                            height: 28,
                            color,
                            opacity: 0.7,
                            "&:hover": { opacity: 1, backgroundColor: "rgba(255,255,255,0.6)" },
                          }}
                        >
                          <VolumeUpIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {selected && (
                <Button
                  fullWidth
                  onClick={goNext}
                  variant="contained"
                  sx={{
                    mt: 2.5,
                    background: "linear-gradient(135deg, #4338ca, #6366f1)",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    py: 1.5,
                    boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
                    "&:hover": { background: "linear-gradient(135deg, #3730a3, #4f46e5)" },
                  }}
                >
                  次へ
                </Button>
              )}

              <Button
                fullWidth
                onClick={finishQuiz}
                sx={{
                  mt: selected ? 1 : 2,
                  color: "#64748b",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                終了する
              </Button>
            </Box>
          )}

          {phase === "finished" && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography sx={{ fontSize: "0.9rem", color: "#64748b", fontWeight: 600, mb: 1 }}>
                結果
              </Typography>
              <Typography sx={{ fontSize: "2.2rem", fontWeight: 800, color: "#1e293b", mb: 3 }}>
                {score} / {answered}
              </Typography>

              <Button
                fullWidth
                onClick={() => setPhase("setup")}
                variant="contained"
                sx={{
                  background: "linear-gradient(135deg, #4338ca, #6366f1)",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.5,
                  mb: 1.5,
                  boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
                  "&:hover": { background: "linear-gradient(135deg, #3730a3, #4f46e5)" },
                }}
              >
                もう一度挑戦する
              </Button>

              {loggedIn ? (
                <Button
                  fullWidth
                  component={NavLink}
                  to="/quiz/mistakes"
                  sx={{ color: "#4338ca", textTransform: "none", fontWeight: 600 }}
                >
                  間違えた問題を見る
                </Button>
              ) : (
                <Button
                  fullWidth
                  onClick={handleLogin}
                  sx={{ color: "#4338ca", textTransform: "none", fontWeight: 600 }}
                >
                  ログインして間違えた問題を記録する
                </Button>
              )}

              {loginFailed && (
                <Typography sx={{ color: "#ef4444", fontSize: "0.78rem", mt: 1.5 }}>
                  ログインに接続できませんでした。時間をおいて再度お試しください。
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Footer />
    </Box>
  );
}
